import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";

const villefrancheSurMer: ChauffeurPage = {
  slug: "villefranche-sur-mer",
  title: "VTC & private chauffeur in Villefranche-sur-Mer",
  subtitle:
    "Private chauffeur in Villefranche-sur-Mer: cruise calls, the bay and the Old Town, a stone's throw from Nice.",
  keywords: [
    "vtc villefranche-sur-mer",
    "private chauffeur villefranche-sur-mer",
    "villefranche cruise chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Villefranche-sur-Mer",
    subtitle:
      "One of the Mediterranean's finest bays and its cruise anchorage: a chauffeur for your calls and stays.",
    keywords: [
      "VTC Villefranche-sur-Mer",
      "cruise call chauffeur",
      "Villefranche bay chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Villefranche-sur-Mer",
    badges: ["Cruise calls", "10 min from Nice", "Bilingual chauffeurs"],
  },
  benefits: {
    heading: "Why a chauffeur in Villefranche-sur-Mer",
    items: [
      {
        title: "Cruise-call specialist",
        description:
          "A deep-water bay where liners anchor: pick-up at the tender landing, return timed to your all-aboard call.",
      },
      {
        title: "Day excursions",
        description:
          "A call of a few hours is enough for Nice, Monaco, Èze or Cap-Ferrat: an optimised circuit to get you back to the ship on time.",
      },
      {
        title: "A preserved Old Town",
        description:
          "Lanes, the Rue Obscure and the Citadelle are largely pedestrian: drop-off as close as possible despite the narrow centre.",
      },
      {
        title: "A stone's throw from Nice",
        description:
          "About ten minutes from Nice airport and centre via the lower corniche, with Cap-Ferrat right next door.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Villefranche-sur-Mer",
    services: [
      {
        title: "Cruise-call transfers",
        text: "Pick-up at the tender landing, a private excursion and a guaranteed return before your all-aboard call on the liner.",
      },
      {
        title: "Private day excursions",
        text: "Nice, Monaco, Èze, Cap-Ferrat: a bespoke itinerary from the bay, timed to the length of your call.",
      },
      {
        title: "Airport transfers",
        text: "Villefranche-sur-Mer ↔ Nice airport with flight tracking, about 20 minutes via the lower corniche.",
      },
      {
        title: "Hotel transfers",
        text: "Drop-off and pick-up at the bay and seafront hotels, with luggage assistance despite the narrow lanes.",
      },
      {
        title: "Hourly & daily hire",
        text: "A dedicated chauffeur for a day on the Riviera, from the Citadelle to the villas of neighbouring Cap-Ferrat.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Harbour & tender landing",
        description:
          "Landing point for cruise passengers coming ashore from the bay.",
      },
      {
        name: "Old Town",
        description:
          "Colourful lanes and quays of the bay, largely pedestrian.",
      },
      {
        name: "Rue Obscure",
        description: "Medieval covered street, an emblem of the Old Town.",
      },
      {
        name: "Citadelle Saint-Elme",
        description: "Sixteenth-century fortress overlooking the harbour.",
      },
      {
        name: "Seafront & beach",
        description: "Hotels and the arc-shaped beach of the bay.",
      },
      {
        name: "Neighbouring Cap-Ferrat",
        description: "Adjoining peninsula, villas and Paloma beach.",
      },
    ],
  },
  generic: {
    heading: "Villefranche-sur-Mer, a deep bay and cruise calls",
    subheading:
      "One of the deepest anchorages in the Mediterranean, where cruise passengers come ashore.",
    paragraphs: [
      "The bay of Villefranche is among the deepest in the Mediterranean: large liners anchor here and bring their passengers ashore by tender, right into the heart of the village. For a call, every minute counts — we pick up at the tender landing and guarantee the return before your all-aboard time.",
      "In a few hours ashore, Nice, Èze, Monaco or Cap-Ferrat are all within easy reach. The itinerary is timed to the exact length of the call, with a safety margin so you never miss the ship. The chauffeur knows the traffic constraints of the lower corniche in season.",
      "The Old Town, with its lanes, the famous Rue Obscure and the Citadelle Saint-Elme, is largely pedestrian. The chauffeur drops you as close as possible to the quays and collects you on request, with no hunt for a space in a tight historic centre.",
    ],
    bullets: ["Return before all-aboard", "Optimised call", "10 min from Nice"],
  },
  process: {
    heading: "Book a chauffeur in Villefranche-sur-Mer",
    steps: [
      "Share your call (ship, landing time, all-aboard time) or your addresses.",
      "We build a circuit that fits the length of the call, with a safety margin.",
      "The chauffeur greets you at the tender landing or at your hotel.",
      "A private excursion at your own pace and a guaranteed return to the ship on time.",
    ],
  },
  faq: [
    {
      q: "Do you collect cruise passengers at the Villefranche tender landing?",
      a: "Yes. Liners anchor in the bay and bring passengers ashore by tender into the heart of the village; we welcome you at the tender landing and time the excursion to your all-aboard call.",
    },
    {
      q: "What can you visit during a call at Villefranche-sur-Mer?",
      a: "Depending on the length of the call, Nice, Èze, Monaco and Cap-Ferrat are all reachable. We build a bespoke private circuit with a comfortable margin for the return to the ship.",
    },
    {
      q: "How long is it from Villefranche-sur-Mer to Nice airport?",
      a: "Around 20 minutes via the lower corniche outside peak hours. We track your flight to adjust the pick-up time.",
    },
    {
      q: "Is it easy to drive in the Old Town of Villefranche?",
      a: "The historic centre, with the Rue Obscure and the Citadelle, is largely pedestrian and narrow. The chauffeur drops you as close as possible to the quays and collects you on request, with no search for parking.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a Nice Airport ⇄ Saint-Jean-Cap-Ferrat transfer",
      href: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
    {
      label: "Book a Monaco ⇄ Nice Airport transfer",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    { label: "All chauffeur-driven routes", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Villefranche-sur-Mer",
    text: "Cruise call, excursion or airport transfer: book your chauffeur on the bay.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Villefranche — cruise-call chauffeur & bay",
    description:
      "Private car service in Villefranche-sur-Mer: cruise calls, Nice/Monaco/Èze excursions, the bay and Old Town. Guaranteed return to ship, 24/7.",
    canonical: "/en/chauffeur/villefranche-sur-mer",
  },
};

export default villefrancheSurMer;
